/**
 * The General tab's derived times: linked groups resolved to chips, the
 * plan chain with the draft spliced in (so "after previous" shows the real
 * start — inside its campaign group), the live drop minutes keyed BY SLOT
 * (a pinned drop sits at its own minute, so a row's position is no key) and
 * their hover labels, in time order.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenAllListsMode } from '@trafficpulse/shared';
import { useMemo } from 'react';

import { useListGroups } from '@/features/list-groups';

import type { ContainerDraft, DropGenPlanDetail } from '../../../../drop-generator.types';
import { type ClockCtx, clockText } from '../../../../utils/clock/dk-label';
import { useCountryClock } from '../../../shared/time/country-clock-context';
import { isPlanned, slotKey, type SlotTime } from '../../../../utils/slot-times';
import { chainTimes } from './chain-times';

export interface GroupRef {
  id: string;
  name: string;
  color: string;
  /** 19/10/2026 — 1 = one list; N = all lists up to N (0 = all), EACH drop to every list or ALTERNATE. */
  listsPerDay?: number;
  allListsMode?: DropGenAllListsMode;
}

export type { PreviousContainer } from './chain-times';

export function useGeneralTimes(draft: ContainerDraft, plan: DropGenPlanDetail, containerId: string | null) {
  const groupRefs = useGroupRefs(draft, plan.countryKey);
  const clock = useCountryClock();
  const { times, previous } = useChainTimes(draft, plan, containerId, clock.runDate);
  const timesByKey = useMemo(
    () => new Map((times?.slotTimes ?? []).map((t) => [t.slotId ?? '', t])),
    [times],
  );
  const ctx: LabelCtx = {
    slotByKey: new Map(draft.slots.map((s, i) => [slotKey(s, i), s])),
    groupNames: new Map(groupRefs.map((g) => [g.id, g.name])),
    defaultAmount: draft.defaultAmount,
    clock,
  };
  const live = (times?.slotTimes ?? []).filter(isPlanned).sort((a, b) => a.minute - b.minute);
  const slotMinutes = live.map((s) => s.minute);
  const slotLabels = live.map((s, i) => dropLabel(s, i, ctx));
  return { groupRefs, times, previous, timesByKey, slotMinutes, slotLabels };
}

/** Linked groups in link order, resolved to name+color. Empty until the
 *  catalog query lands (the chips simply stay hidden that long). */
function useGroupRefs(draft: ContainerDraft, countryKey: string): GroupRef[] {
  const listGroups = useListGroups(countryKey);
  return useMemo(() => {
    const byId = new Map((listGroups.data ?? []).map((g) => [g.id, g]));
    return draft.groups.flatMap((ref) => {
      const g = byId.get(ref.listGroupId);
      return g ? [{ id: g.id, name: g.name, color: g.color, listsPerDay: ref.listsPerDay, allListsMode: ref.allListsMode }] : [];
    });
  }, [draft.groups, listGroups.data]);
}

/** The plan chain with the draft spliced in, on the board's day (chain-times.ts). */
function useChainTimes(draft: ContainerDraft, plan: DropGenPlanDetail, containerId: string | null, runDate?: string | null) {
  return useMemo(() => chainTimes(draft, plan, containerId, runDate), [draft, plan, containerId, runDate]);
}

interface LabelCtx {
  slotByKey: Map<string, { amount: number | null; listGroupIds: string[] }>;
  groupNames: Map<string, string>;
  defaultAmount: number;
  /** Danish first (15/10/2026): "Drop 1 · 05:00 🇩🇰 · 🇦🇺 14:00". */
  clock: ClockCtx;
}

function dropLabel(time: SlotTime, i: number, ctx: LabelCtx): string {
  const slot = ctx.slotByKey.get(time.slotId ?? '');
  const amount = slot?.amount ?? ctx.defaultAmount;
  const groups = (slot?.listGroupIds ?? []).map((g) => ctx.groupNames.get(g)).filter(Boolean).join(' + ');
  return `Drop ${i + 1} · ${clockText(time.minute, ctx.clock)} · ${amount.toLocaleString('en-US')} SMS${groups ? ` · ${groups}` : ''}`;
}
